import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  AppBar,
  Toolbar,
  TextField,
  InputAdornment,
  Button
} from '@mui/material';
import {
  LocalShipping,
  Inventory,
  LocationOn,
  CalendarToday,
  TrendingUp,
  OpenInFull,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { styled } from '@mui/material/styles';
import moment from 'moment-timezone';
import CloseIcon from '@mui/icons-material/Close';

const ChartCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '& .MuiCardContent-root': {
    padding: theme.spacing(1.5),
    '&:last-child': {
      paddingBottom: theme.spacing(1.5)
    }
  }
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  '& .MuiCardContent-root': {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2)
    }
  }
}));

// Scrollable chart container
const ScrollableChartBox = styled(Box)(({ theme }) => ({
  height: 320,
  overflow: 'auto',
  padding: theme.spacing(0.5),
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px'
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px'
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
    '&:hover': {
      background: '#555'
    }
  }
}));

const PalletScoreCard = ({ shipments }) => {
  const [sortConfig, setSortConfig] = useState({
    field: 'deliveryDate',
    direction: 'desc'
  });
  const [expandedChart, setExpandedChart] = useState(null);
  const [activeSearchTerm, setActiveSearchTerm] = useState(''); // Only updates on Apply or Enter
  const searchInputRef = useRef('');

  // Filter out TL shipments once at the beginning
  const nonTlShipments = useMemo(() => {
    return shipments.filter(s => s.shipmentType !== 'TL');
  }, [shipments]);

  console.log(nonTlShipments, 'non-TL shipments');

  // Pre-process deliveries data once
  const baseDeliveries = useMemo(() => {
    return nonTlShipments
      .filter(s => Number(s.totalUnits) > 0)
      .map(s => ({
        id: s.id,
        deliveryDate: s.actualDeliveryDate,
        deliveryTimestamp: s.actualDeliveryDate
          ? new Date(s.actualDeliveryDate).getTime()
          : 0,
        carrier: s.carrierName || 'Unknown',
        carrierLower: (s.carrierName || 'unknown').toLowerCase(),
        originLocation:
          `${s.originCity || ''}, ${s.originState || ''}`.replace(
            /^, |, $/g,
            ''
          ) || 'Unknown',
        originLocationLower:
          `${s.originCity || ''}, ${s.originState || ''}`
            .replace(/^, |, $/g, '')
            .toLowerCase() || 'unknown',
        pallets: Number(s.totalUnits) || 0,
        destination:
          `${s.destinationCity || ''}, ${s.destinationState || ''}`.replace(
            /^, |, $/g,
            ''
          ) || 'Unknown',
        destinationLower:
          `${s.destinationCity || ''}, ${s.destinationState || ''}`
            .replace(/^, |, $/g, '')
            .toLowerCase() || 'unknown',
        proNumber: s.proNumber || '-',
        proNumberLower: (s.proNumber || '-').toLowerCase(),
        bolNumber: s.bolNumber || '-',
        bolNumberLower: (s.bolNumber || '-').toLowerCase()
      }));
  }, [nonTlShipments]);

  // Calculate statistics using non-TL shipments
  const statistics = useMemo(() => {
    const deliveredShipments = nonTlShipments;

    const totalPallets = deliveredShipments.reduce(
      (sum, s) => sum + (Number(s.totalUnits) || 0),
      0
    );

    const totalShipments = deliveredShipments.length;

    const uniqueCarriers = new Set(
      deliveredShipments.map(s => s.carrierName).filter(Boolean)
    ).size;

    const avgPalletsPerShipment =
      totalShipments > 0 ? (totalPallets / totalShipments).toFixed(1) : 0;

    // Calculate pallets by carrier (all carriers, no limits)
    const palletsByCarrier = {};
    const shipmentsByCarrier = {};

    deliveredShipments.forEach(shipment => {
      const carrier = shipment.carrierName || 'Unknown';
      const pallets = Number(shipment.totalUnits) || 0;

      if (!palletsByCarrier[carrier]) {
        palletsByCarrier[carrier] = 0;
        shipmentsByCarrier[carrier] = 0;
      }

      palletsByCarrier[carrier] += pallets;
      shipmentsByCarrier[carrier] += 1;
    });

    // Calculate pallets by location (origin city) - all locations
    const palletsByLocation = {};
    const shipmentsByLocation = {};

    deliveredShipments.forEach(shipment => {
      const location = shipment.originCity || 'Unknown';
      const pallets = Number(shipment.totalUnits) || 0;

      if (!palletsByLocation[location]) {
        palletsByLocation[location] = 0;
        shipmentsByLocation[location] = 0;
      }

      palletsByLocation[location] += pallets;
      shipmentsByLocation[location] += 1;
    });

    // Calculate daily pallet deliveries (all dates, no limits)
    const dailyPallets = {};

    deliveredShipments.forEach(shipment => {
      if (shipment.actualDeliveryDate) {
        const date = moment(shipment.actualDeliveryDate).format('YYYY-MM-DD');
        const pallets = Number(shipment.totalUnits) || 0;

        if (!dailyPallets[date]) {
          dailyPallets[date] = 0;
        }

        dailyPallets[date] += pallets;
      }
    });

    // Get all unique dates and sort them chronologically
    const allDates = Object.keys(dailyPallets).sort();

    // Format dates for chart display
    const chartDates = allDates.map(date => moment(date).format('MMM DD YYYY'));
    const chartData = allDates.map(date => dailyPallets[date] || 0);

    // Get all carriers sorted by pallets (no slicing)
    const allCarriers = Object.entries(palletsByCarrier)
      .sort(([, a], [, b]) => b - a)
      .map(([carrier, pallets]) => ({
        carrier,
        pallets,
        shipments: shipmentsByCarrier[carrier] || 0
      }));

    // Get all locations sorted by pallets (no slicing)
    const allLocations = Object.entries(palletsByLocation)
      .sort(([, a], [, b]) => b - a)
      .map(([location, pallets]) => ({
        location,
        pallets,
        shipments: shipmentsByLocation[location] || 0
      }));

    return {
      totalPallets,
      totalShipments,
      uniqueCarriers,
      avgPalletsPerShipment,
      allCarriers,
      allLocations,
      dailyChartData: {
        dates: chartDates,
        pallets: chartData
      },
      allCarriersData: {
        carriers: Object.keys(palletsByCarrier),
        pallets: Object.values(palletsByCarrier)
      },
      allLocationsData: {
        locations: Object.keys(palletsByLocation),
        pallets: Object.values(palletsByLocation)
      }
    };
  }, [nonTlShipments]);

  // Handle sort for table
  const handleSort = useCallback(field => {
    setSortConfig(prev => ({
      field,
      direction:
        prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Apply search filter
  const applySearch = useCallback(() => {
    setActiveSearchTerm(searchInputRef.current);
  }, []);

  // Handle Enter key
  const handleKeyPress = useCallback(
    e => {
      if (e.key === 'Enter') {
        applySearch();
      }
    },
    [applySearch]
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    searchInputRef.current = '';
    setActiveSearchTerm('');
    // Also clear the input field value
    const input = document.querySelector(
      'input[placeholder="Search deliveries..."]'
    );
    if (input) {
      input.value = '';
    }
  }, []);

  // Get filtered deliveries based on active search term
  const filteredDeliveries = useMemo(() => {
    if (!activeSearchTerm) {
      return baseDeliveries;
    }

    const searchLower = activeSearchTerm.toLowerCase();

    return baseDeliveries.filter(
      delivery =>
        delivery.carrierLower.includes(searchLower) ||
        delivery.originLocationLower.includes(searchLower) ||
        delivery.destinationLower.includes(searchLower) ||
        delivery.proNumberLower.includes(searchLower) ||
        delivery.bolNumberLower.includes(searchLower) ||
        delivery.pallets.toString().includes(activeSearchTerm)
    );
  }, [baseDeliveries, activeSearchTerm]);

  // Get sorted pallet deliveries
  const palletDeliveries = useMemo(() => {
    return [...filteredDeliveries].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.field) {
        case 'deliveryDate':
          aValue = a.deliveryTimestamp;
          bValue = b.deliveryTimestamp;
          break;
        case 'carrier':
          aValue = a.carrier;
          bValue = b.carrier;
          break;
        case 'originLocation':
          aValue = a.originLocation;
          bValue = b.originLocation;
          break;
        case 'pallets':
          aValue = a.pallets;
          bValue = b.pallets;
          break;
        case 'destination':
          aValue = a.destination;
          bValue = b.destination;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredDeliveries, sortConfig]);

  // Format number with commas
  const formatNumber = useCallback(num => {
    return num?.toLocaleString() || '0';
  }, []);

  // Prepare carrier chart data - ALL carriers from non-TL shipments
  const carrierChartData = useMemo(() => {
    if (statistics.allCarriers.length === 0) {
      return { series: [], categories: [] };
    }

    const sortedCarriers = [...statistics.allCarriers].sort(
      (a, b) => b.pallets - a.pallets
    );

    return {
      series: [
        {
          name: 'Pallets',
          data: sortedCarriers.map(item => item.pallets)
        }
      ],
      categories: sortedCarriers.map(item => {
        const name = item.carrier;
        return name.length > 25 ? name.substring(0, 23) + '...' : name;
      })
    };
  }, [statistics.allCarriers]);

  // Prepare location chart data - Top 10 locations from non-TL shipments
  const locationChartData = useMemo(() => {
    if (statistics.allLocations.length === 0) {
      return { series: [], categories: [] };
    }

    const sortedLocations = [...statistics.allLocations]
      .sort((a, b) => b.pallets - a.pallets)
      .slice(0, 10);

    return {
      series: [
        {
          name: 'Pallets',
          data: sortedLocations.map(item => item.pallets)
        }
      ],
      categories: sortedLocations.map(item => {
        const name = item.location;
        return name.length > 25 ? name.substring(0, 23) + '...' : name;
      })
    };
  }, [statistics.allLocations]);

  return (
    <Box sx={{ p: 1 }}>
      {/* Statistics Cards */}
      <Dialog
        fullScreen
        open={!!expandedChart}
        onClose={() => setExpandedChart(null)}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton
              edge='start'
              color='inherit'
              onClick={() => setExpandedChart(null)}
              aria-label='close'
            >
              <CloseIcon />
            </IconButton>
            <Typography
              sx={{ ml: 2, flex: 1, color: '#ffffff !important' }}
              variant='h6'
              component='div'
            >
              {expandedChart === 'dailyPalletDeliveries' &&
                'Daily Pallet Deliveries - Detailed View'}
              {expandedChart === 'allCarriersPallets' &&
                'All Carriers by Pallets - Detailed View'}
              {expandedChart === 'allLocationsPallets' &&
                'Top 10 Locations by Pallets - Detailed View'}
            </Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 3, height: 'calc(100vh - 64px)' }}>
          {expandedChart === 'dailyPalletDeliveries' && (
            <ReactApexChart
              options={{
                chart: {
                  type: 'bar',
                  height: '100%',
                  toolbar: { show: true }
                },
                plotOptions: {
                  bar: {
                    borderRadius: 4,
                    columnWidth: '60%',
                    dataLabels: { position: 'top' }
                  }
                },
                dataLabels: {
                  enabled: true,
                  formatter: val => formatNumber(val),
                  style: {
                    fontSize: '12px',
                    fontWeight: 'bold',
                    colors: ['#304758']
                  }
                },
                xaxis: {
                  categories: statistics.dailyChartData.dates,
                  labels: {
                    rotate: -45,
                    style: { fontSize: '12px' }
                  }
                },
                yaxis: {
                  title: { text: 'Number of Pallets' },
                  labels: {
                    formatter: val => formatNumber(val)
                  }
                },
                colors: ['#008FFB'],
                tooltip: {
                  y: {
                    formatter: val => formatNumber(val) + ' pallets'
                  }
                }
              }}
              series={[
                {
                  name: 'Pallets Delivered',
                  data: statistics.dailyChartData.pallets
                }
              ]}
              type='bar'
              height='100%'
            />
          )}

          {expandedChart === 'allCarriersPallets' && (
            <ReactApexChart
              options={{
                chart: {
                  type: 'bar',
                  height: '100%',
                  toolbar: { show: true }
                },
                plotOptions: {
                  bar: {
                    horizontal: true,
                    barHeight: '70%',
                    borderRadius: 4,
                    dataLabels: { position: 'right' }
                  }
                },
                dataLabels: {
                  enabled: true,
                  formatter: val => formatNumber(val),
                  offsetX: 30,
                  style: {
                    fontSize: '12px',
                    fontWeight: 'bold',
                    colors: ['#000']
                  }
                },
                xaxis: {
                  labels: {
                    formatter: val => formatNumber(val)
                  }
                },
                yaxis: {
                  categories: carrierChartData.categories,
                  labels: {
                    style: { fontSize: '12px', fontWeight: 500 }
                  }
                },
                colors: ['#00E396'],
                tooltip: {
                  y: {
                    formatter: (val, { dataPointIndex }) => {
                      const fullCarrierName =
                        statistics.allCarriers[dataPointIndex]?.carrier || '';
                      return `${formatNumber(val)} pallets (${fullCarrierName})`;
                    }
                  }
                }
              }}
              series={carrierChartData.series}
              type='bar'
              height='100%'
            />
          )}

          {expandedChart === 'allLocationsPallets' && (
            <ReactApexChart
              options={{
                chart: {
                  type: 'bar',
                  height: '100%',
                  toolbar: { show: true }
                },
                plotOptions: {
                  bar: {
                    horizontal: true,
                    barHeight: '70%',
                    borderRadius: 4,
                    dataLabels: { position: 'right' }
                  }
                },
                dataLabels: {
                  enabled: true,
                  formatter: val => formatNumber(val),
                  offsetX: 30,
                  style: {
                    fontSize: '12px',
                    fontWeight: 'bold',
                    colors: ['#000']
                  }
                },
                xaxis: {
                  labels: {
                    formatter: val => formatNumber(val)
                  }
                },
                yaxis: {
                  categories: locationChartData.categories,
                  labels: {
                    style: { fontSize: '12px', fontWeight: 500 }
                  }
                },
                colors: ['#FEB019'],
                tooltip: {
                  y: {
                    formatter: (val, { dataPointIndex }) => {
                      const fullLocationName =
                        statistics.allLocations[dataPointIndex]?.location || '';
                      const shipments =
                        statistics.allLocations[dataPointIndex]?.shipments || 0;
                      return `${formatNumber(val)} pallets from ${fullLocationName} (${shipments} shipments)`;
                    }
                  }
                }
              }}
              series={locationChartData.series}
              type='bar'
              height='100%'
            />
          )}
        </Box>
      </Dialog>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Inventory sx={{ mr: 1 }} />
                <Typography variant='body2' sx={{ opacity: 0.8 }}>
                  Total Pallets Delivered
                </Typography>
              </Box>
              <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                {formatNumber(statistics.totalPallets)}
              </Typography>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>
                Across {statistics.totalShipments} shipments
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocalShipping sx={{ mr: 1 }} />
                <Typography variant='body2' sx={{ opacity: 0.8 }}>
                  Unique Carriers
                </Typography>
              </Box>
              <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                {statistics.uniqueCarriers}
              </Typography>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>
                Active carriers
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp sx={{ mr: 1 }} />
                <Typography variant='body2' sx={{ opacity: 0.8 }}>
                  Avg Pallets/Shipment
                </Typography>
              </Box>
              <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                {statistics.avgPalletsPerShipment}
              </Typography>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>
                Per delivery
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocationOn sx={{ mr: 1 }} />
                <Typography variant='body2' sx={{ opacity: 0.8 }}>
                  Active Locations
                </Typography>
              </Box>
              <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                {statistics.allLocations.length}
              </Typography>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>
                Shipping origins
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* All Carriers by Pallets Chart */}
        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1
                }}
              >
                <Typography
                  variant='h6'
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <LocalShipping color='success' />
                  All Carriers by Pallets
                </Typography>
                <Tooltip title='View Expanded'>
                  <IconButton
                    size='small'
                    onClick={() =>
                      setExpandedChart && setExpandedChart('allCarriersPallets')
                    }
                    sx={{
                      bgcolor: 'success.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'success.dark' }
                    }}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Box>
              <ScrollableChartBox>
                {carrierChartData.categories.length > 0 ? (
                  <Box sx={{ minWidth: 400, height: 300 }}>
                    <ReactApexChart
                      options={{
                        chart: {
                          type: 'bar',
                          height: '100%',
                          toolbar: { show: true }
                        },
                        plotOptions: {
                          bar: {
                            horizontal: true,
                            barHeight: '70%',
                            borderRadius: 4,
                            dataLabels: { position: 'right' }
                          }
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: val => formatNumber(val),
                          offsetX: 30,
                          style: {
                            fontSize: '11px',
                            fontWeight: 'bold',
                            colors: ['#000']
                          }
                        },
                        xaxis: {
                          categories: carrierChartData.categories,
                          labels: {
                            formatter: val => formatNumber(val)
                          }
                        },
                        yaxis: {
                          labels: {
                            style: {
                              fontSize: '11px',
                              fontWeight: 500
                            },
                            formatter: (val, index) => {
                              return carrierChartData.categories[index] || val;
                            }
                          }
                        },
                        colors: ['#00E396'],
                        tooltip: {
                          y: {
                            formatter: (val, { dataPointIndex }) => {
                              const fullCarrierName =
                                statistics.allCarriers[dataPointIndex]
                                  ?.carrier || '';
                              return `${formatNumber(val)} pallets (${fullCarrierName})`;
                            }
                          }
                        },
                        grid: {
                          padding: {
                            left: 20,
                            right: 40
                          }
                        }
                      }}
                      series={carrierChartData.series}
                      type='bar'
                      height={280}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%'
                    }}
                  >
                    <Typography color='text.secondary'>
                      No carrier data available
                    </Typography>
                  </Box>
                )}
              </ScrollableChartBox>
            </CardContent>
          </ChartCard>
        </Grid>

        {/* All Locations by Pallets Chart */}
        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1
                }}
              >
                <Typography
                  variant='h6'
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <LocationOn color='warning' />
                  Top 10 Locations by Pallets
                </Typography>
                <Tooltip title='View Expanded'>
                  <IconButton
                    size='small'
                    onClick={() =>
                      setExpandedChart &&
                      setExpandedChart('allLocationsPallets')
                    }
                    sx={{
                      bgcolor: 'warning.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'warning.dark' }
                    }}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Box>
              <ScrollableChartBox>
                {locationChartData.categories.length > 0 ? (
                  <Box sx={{ minWidth: 400, height: 300 }}>
                    <ReactApexChart
                      options={{
                        chart: {
                          type: 'bar',
                          height: '100%',
                          toolbar: { show: true }
                        },
                        plotOptions: {
                          bar: {
                            horizontal: true,
                            barHeight: '70%',
                            borderRadius: 4,
                            dataLabels: { position: 'right' }
                          }
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: val => formatNumber(val),
                          offsetX: 30,
                          style: {
                            fontSize: '11px',
                            fontWeight: 'bold',
                            colors: ['#000']
                          }
                        },
                        xaxis: {
                          categories: locationChartData.categories,
                          labels: {
                            formatter: val => formatNumber(val)
                          }
                        },
                        yaxis: {
                          labels: {
                            style: {
                              fontSize: '11px',
                              fontWeight: 500
                            },
                            formatter: (val, index) => {
                              return locationChartData.categories[index] || val;
                            }
                          }
                        },
                        colors: ['#FEB019'],
                        tooltip: {
                          y: {
                            formatter: (val, { dataPointIndex }) => {
                              const fullLocationName =
                                statistics.allLocations[dataPointIndex]
                                  ?.location || '';
                              const shipments =
                                statistics.allLocations[dataPointIndex]
                                  ?.shipments || 0;
                              return `${formatNumber(val)} pallets from ${fullLocationName} (${shipments} shipments)`;
                            }
                          }
                        },
                        grid: {
                          padding: {
                            left: 20,
                            right: 40
                          }
                        }
                      }}
                      series={locationChartData.series}
                      type='bar'
                      height={280}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%'
                    }}
                  >
                    <Typography color='text.secondary'>
                      No location data available
                    </Typography>
                  </Box>
                )}
              </ScrollableChartBox>
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Daily Pallet Deliveries Chart - Full Width with Scroll */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <ChartCard>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1
                }}
              >
                <Typography
                  variant='h6'
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <CalendarToday color='primary' />
                  Daily Pallet Deliveries (All Time)
                </Typography>
                <Tooltip title='View Expanded'>
                  <IconButton
                    size='small'
                    onClick={() =>
                      setExpandedChart &&
                      setExpandedChart('dailyPalletDeliveries')
                    }
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Box>
              <ScrollableChartBox>
                {statistics.dailyChartData.dates.length > 0 ? (
                  <Box
                    sx={{
                      minWidth: statistics.dailyChartData.dates.length * 40,
                      height: 300
                    }}
                  >
                    <ReactApexChart
                      options={{
                        chart: {
                          type: 'bar',
                          height: '100%',
                          toolbar: {
                            show: true,
                            tools: {
                              download: true,
                              selection: true,
                              zoom: true,
                              pan: true
                            }
                          }
                        },
                        plotOptions: {
                          bar: {
                            borderRadius: 4,
                            columnWidth: '60%',
                            dataLabels: { position: 'top' }
                          }
                        },
                        dataLabels: {
                          enabled: false
                        },
                        xaxis: {
                          categories: statistics.dailyChartData.dates,
                          labels: {
                            rotate: -45,
                            rotateAlways: false,
                            style: { fontSize: '10px' }
                          },
                          tickAmount: 20
                        },
                        yaxis: {
                          title: {
                            text: 'Number of Pallets'
                          },
                          labels: {
                            formatter: val => Math.round(val)
                          }
                        },
                        colors: ['#008FFB'],
                        tooltip: {
                          y: {
                            formatter: val => val + ' pallets'
                          }
                        },
                        grid: {
                          padding: { left: 10, right: 10 }
                        },
                        title: {
                          align: 'left',
                          style: { fontSize: '12px' }
                        }
                      }}
                      series={[
                        {
                          name: 'Pallets Delivered',
                          data: statistics.dailyChartData.pallets
                        }
                      ]}
                      type='bar'
                      height={280}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%'
                    }}
                  >
                    <Typography color='text.secondary'>
                      No daily data available
                    </Typography>
                  </Box>
                )}
              </ScrollableChartBox>
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Detailed Pallet Deliveries Table with Search */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ChartCard>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2
                }}
              >
                <Typography variant='h6'>
                  Detailed Pallet Deliveries (All Time)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size='small'
                    placeholder='Search deliveries...'
                    defaultValue={activeSearchTerm}
                    onChange={e => {
                      searchInputRef.current = e.target.value;
                    }}
                    onKeyPress={handleKeyPress}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <SearchIcon fontSize='small' />
                        </InputAdornment>
                      )
                    }}
                    sx={{ width: 250 }}
                  />
                  <Button
                    variant='contained'
                    size='small'
                    onClick={applySearch}
                    sx={{ minWidth: 'auto' }}
                  >
                    Search
                  </Button>
                  {activeSearchTerm && (
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={handleClearSearch}
                      sx={{ minWidth: 'auto' }}
                    >
                      Clear
                    </Button>
                  )}
                </Box>
              </Box>
              <TableContainer
                component={Paper}
                variant='outlined'
                sx={{
                  maxHeight: 400,
                  overflow: 'auto',
                  '& .MuiTableCell-stickyHeader': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                <Table stickyHeader size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={sortConfig.field === 'deliveryDate'}
                          direction={sortConfig.direction}
                          onClick={() => handleSort('deliveryDate')}
                        >
                          Delivery Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortConfig.field === 'carrier'}
                          direction={sortConfig.direction}
                          onClick={() => handleSort('carrier')}
                        >
                          Carrier
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortConfig.field === 'originLocation'}
                          direction={sortConfig.direction}
                          onClick={() => handleSort('originLocation')}
                        >
                          Origin Location
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortConfig.field === 'destination'}
                          direction={sortConfig.direction}
                          onClick={() => handleSort('destination')}
                        >
                          Destination
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align='right'>
                        <TableSortLabel
                          active={sortConfig.field === 'pallets'}
                          direction={sortConfig.direction}
                          onClick={() => handleSort('pallets')}
                        >
                          Pallets
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>PRO #</TableCell>
                      <TableCell>BOL #</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {palletDeliveries.map(delivery => (
                      <TableRow key={delivery.id} hover>
                        <TableCell>
                          {delivery.deliveryDate
                            ? moment(delivery.deliveryDate).format(
                                'MMM DD, YYYY'
                              )
                            : '-'}
                        </TableCell>
                        <TableCell>{delivery.carrier}</TableCell>
                        <TableCell>{delivery.originLocation}</TableCell>
                        <TableCell>{delivery.destination}</TableCell>
                        <TableCell align='right' sx={{ fontWeight: 'bold' }}>
                          {delivery.pallets}
                        </TableCell>
                        <TableCell>{delivery.proNumber}</TableCell>
                        <TableCell>{delivery.bolNumber}</TableCell>
                      </TableRow>
                    ))}
                    {palletDeliveries.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align='center' sx={{ py: 3 }}>
                          <Alert severity='info'>
                            {activeSearchTerm
                              ? 'No matching deliveries found'
                              : 'No pallet delivery data available'}
                          </Alert>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {palletDeliveries.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 1
                  }}
                >
                  <Typography variant='caption' color='text.secondary'>
                    Showing {palletDeliveries.length} of {baseDeliveries.length}{' '}
                    deliveries
                  </Typography>
                  {activeSearchTerm && (
                    <Typography variant='caption' color='text.secondary'>
                      Search results for "{activeSearchTerm}"
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PalletScoreCard;
